import { Injectable } from '@angular/core';
import { PREVIEW } from '../preview/preview-mode';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth.model';
import { Brand, BrandTheme, FALLBACK_BRAND, SocialLink } from '../models/brand.model';
import { FulfillmentMode } from '../models/order.model';
import { Banner, HomeCollection, HomeItem, StoreBranch, StorefrontConfig, STOREFRONT } from '../models/storefront.model';
import { InstallService } from './install.service';

const SOCIAL_ICON: Record<string, string> = {
  instagram: 'mdi:instagram', facebook: 'mdi:facebook', 'facebook-messenger': 'mdi:facebook-messenger',
  messenger: 'mdi:facebook-messenger', tiktok: 'ic:baseline-tiktok',
  whatsapp: 'mdi:whatsapp', x: 'mdi:twitter', twitter: 'mdi:twitter',
  youtube: 'mdi:youtube', snapchat: 'mdi:snapchat', telegram: 'mdi:telegram',
  linkedin: 'mdi:linkedin', pinterest: 'mdi:pinterest', threads: 'mdi:at',
  website: 'mdi:web', web: 'mdi:web', email: 'mdi:email',
};
/** Iconify name for a platform, case/space-insensitive; generic link icon as a last resort. */
function socialIcon(platform: string): string {
  const key = (platform || '').toLowerCase().trim().replace(/\s+/g, '-');
  return SOCIAL_ICON[key] || 'solar:link-circle-bold';
}
const SECTION_LABEL: Record<string, string> = Object.fromEntries(
  STOREFRONT.homepage.sections.map((s) => [s.id, s.label]),
);

function mapOrderType(t: string): FulfillmentMode | null {
  if (t === 'delivery') return 'delivery';
  if (t === 'pickup' || t === 'pickup') return 'pickup';
  if (t === 'dine_in' || t === 'dinein' || t === 'dine-in') return 'dinein';
  return null;
}
function cap(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

/**
 * Loads the published storefront config from `/api/v1/brand` (theme, identity,
 * hero, sections, social, commerce, supported order types) and exposes it to
 * ConfigService / DataService. Loaded via APP_INITIALIZER before bootstrap, so
 * the whole app renders from the published config; on failure it stays null and
 * the app falls back to the bundled defaults.
 */
@Injectable({ providedIn: 'root' })
export class StorefrontService {
  private readonly base = environment.apiBase;

  theme: BrandTheme | null = null;
  storefront: StorefrontConfig | null = null;
  brand: Brand | null = null;
  supportedOrderTypes: FulfillmentMode[] = [];
  announcement: string | null = null;
  /** The announcement bar's messages — the admin stores one per line. */
  announcements: string[] = [];
  banners: Banner[] = [];
  collections: HomeCollection[] = [];
  branches: StoreBranch[] = [];
  /** operator flag catalogue: key → { label, color } for item badges */
  flagDefs: Record<string, { label: string; color?: string }> = {};
  loaded = false;
  maintenance = false;
  /** The API has no brand for this address (`brand_not_found`) — the app shows a "store not found" page. */
  notFound = false;
  maintenanceMessage: string | null = null;
  favicon: string | null = null;
  /** Brand-wide social username shown as "@handle" above the social links (no "@"). */
  socialHandle = '';
  /** Whether the brand enables promo/offer codes (shows the promo input at checkout). */
  offerEnabled = false;
  /** Published commerce config (currency, order types, offers_enabled, …). */
  commerce: any = {};

  constructor(private http: HttpClient, private install: InstallService) {}

  collectionByType(type: string): HomeCollection | undefined {
    return this.collections.find((c) => c.type === type);
  }

  /** Store branches that support a given fulfillment mode (delivery/pickup/dinein). */
  branchesFor(mode: FulfillmentMode): { branch: StoreBranch; eta: number | null; scheduling: boolean }[] {
    const wanted = mode === 'pickup' ? ['pickup', 'pickup'] : mode === 'dinein' ? ['dine_in', 'dinein'] : ['delivery'];
    return this.branches
      .map((b) => ({ b, ot: b.orderTypes.find((o) => wanted.includes(o.type)) }))
      .filter((x) => !!x.ot)
      .map((x) => ({ branch: x.b, eta: x.ot!.estimatedMinutes, scheduling: x.ot!.schedulingEnabled }));
  }

  /** Fetch a collection's items (name/price/image) from its items endpoint. */
  collectionItems(url: string): Observable<HomeItem[]> {
    // Route the absolute items_url through the dev proxy in development.
    const path = environment.apiBase ? url : url.replace(/^https?:\/\/[^/]+/, '');
    return this.http.get<ApiEnvelope<{ items: any[] }>>(path).pipe(
      map((res) => (res.data.items || [])
        .map((it) => it.menu_item)
        .filter(Boolean)
        .map((mi: any): HomeItem => ({
          id: mi.id,
          name: mi.name,
          description: mi.description ?? null,
          price: Math.round((mi.price_from ?? 0) ), // API price is in minor units
          image: mi.image_url ?? (mi.images && mi.images[0]) ?? null,
        }))),
      catchError(() => of([])),
    );
  }

  /** The published GET /api/v1/brand data — the base a preview draft is applied over. */
  private published: any = null;
  /** Fires after a preview draft is applied (the shell re-renders the screen). */
  readonly changed$ = new Subject<void>();

  /**
   * The admin origin allowed to embed this brand's live preview — optional `adminOrigin`
   * (or `admin_origin`) in the brand config, at the top level or in its `storefront` block.
   * Null when the brand config has none (the preview then falls back to the environment patterns).
   */
  get adminOrigin(): string | null {
    const d = this.published || {};
    const s = d.storefront || {};
    return d.adminOrigin ?? d.admin_origin ?? s.adminOrigin ?? s.admin_origin ?? null;
  }

  private loadedResolve!: () => void;
  private readonly brandSettled = new Promise<void>((r) => (this.loadedResolve = r));
  /** Settles once GET /api/v1/brand has answered (or failed). */
  whenLoaded(): Promise<void> { return this.brandSettled; }

  load(): Promise<void> {
    return firstValueFrom(this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/brand`))
      .then((res) => { this.published = res.data; this.apply(res.data); })
      .catch((err) => {
        // Live preview: render the draft even when the brand API is unreachable.
        if (PREVIEW.active) this.published = { storefront: {} };
        // No brand at this address → "store not found" page (never a stand-in brand).
        if (err?.error?.error_code === 'brand_not_found' || err?.status === 404) {
          this.notFound = true;
          document.title = 'Store not found';
          return;
        }
        // Fall back to bundled defaults (mock brand), but make the failure visible —
        // e.g. running without the dev proxy, or a missing/incorrect brandSubdomain.
        console.warn('[storefront] /api/v1/brand failed to load — showing bundled mock brand.', err);
      })
      .finally(() => {
        // A preview draft that arrived while the brand was loading.
        const draft = this.pendingDraft;
        this.pendingDraft = null;
        if (draft) this.applyDraft(draft);
        this.loadedResolve();
      });
  }

  /** A preview draft received before the published config loaded. */
  private pendingDraft: Record<string, any> | null = null;

  /**
   * Live preview: apply the admin's draft `storefront` block over the published
   * one. Nested sections (identity, hero, story, …) merge one level deep, so a
   * draft only needs the presentational keys it owns; everything else (menu
   * collections, branches, commerce, flags) stays as published.
   */
  applyDraft(draft: Record<string, any>): void {
    // Still loading: keep the latest draft, load() applies it when done.
    if (!this.published) { this.pendingDraft = draft; return; }
    const base = this.published.storefront || {};
    const storefront: Record<string, any> = { ...base };
    for (const [key, value] of Object.entries(draft)) {
      const isObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
      storefront[key] = isObject(value) && isObject(base[key]) ? { ...base[key], ...value } : value;
    }
    this.apply({ ...this.published, storefront });
    this.changed$.next();
  }

  private apply(data: any): void {
    const s = data?.storefront;
    if (!s) return;
    this.maintenance = !!s.maintenance?.mode;
    this.maintenanceMessage = s.maintenance?.message || null;
    this.commerce = s.commerce || {};
    this.offerEnabled = !!(this.commerce.offers_enabled ?? this.commerce.offer_enabled ?? s.offer_enabled);

    if (s.theme) {
      this.theme = {
        brand: s.theme.brand, deep: s.theme.deep, l300: s.theme.light,
        ink: s.theme.ink, display: s.theme.display_font || 'Plus Jakarta Sans',
      };
      loadGoogleFont(this.theme.display);
    }

    const social: SocialLink[] = (s.social_links || []).map((x: any) => ({
      id: x.platform,
      label: x.label || cap(x.platform),
      handle: x.handle || '',
      icon: socialIcon(x.platform),
      url: x.url || '#',
    }));

    const heroSrc = s.hero || {};
    this.storefront = {
      hotlineHours: s.identity?.hotline_hours || STOREFRONT.hotlineHours,
      footerText: String(s.footer_text ?? s.footer?.text ?? s.identity?.footer_text ?? '').trim(),
      hero: {
        badge: heroSrc.badge ?? STOREFRONT.hero.badge,
        title1: heroSrc.title1 ?? STOREFRONT.hero.title1,
        title2: heroSrc.title2 ?? STOREFRONT.hero.title2,
        subtitle: heroSrc.subtitle ?? STOREFRONT.hero.subtitle,
        cta: heroSrc.cta ?? STOREFRONT.hero.cta,
        image: heroSrc.image_url ?? null,
        video: heroSrc.video_url ?? null,
      },
      story: {
        ...STOREFRONT.story, ...(s.story || {}),
        image: s.story?.image_url ?? s.story_image_url ?? legacyStoryUrl(s.story?.media ?? s.story_media, 'image'),
        video: s.story?.video_url ?? s.story_video_url ?? legacyStoryUrl(s.story?.media ?? s.story_media, 'video'),
      },
      loyalty: s.loyalty || STOREFRONT.loyalty,
      social,
      legal: {
        terms: s.legal?.terms || STOREFRONT.legal.terms,
        privacy: s.legal?.privacy || STOREFRONT.legal.privacy,
      },
      homepage: {
        enabled: s.homepage?.enabled !== false,
        sections: (s.homepage?.sections || STOREFRONT.homepage.sections).map((x: any) => ({
          id: x.id, on: x.on !== false, label: SECTION_LABEL[x.id] || x.id,
        })),
      },
    };

    // Order types the storefront offers — the brand's commerce.enabled_order_types
    // is authoritative (a branch may not expose dine-in even when it's enabled),
    // falling back to whatever the embedded branches list.
    const enabled: FulfillmentMode[] = (s.commerce?.enabled_order_types || [])
      .map((t: string) => mapOrderType(t))
      .filter((m: FulfillmentMode | null): m is FulfillmentMode => !!m);
    if (enabled.length) {
      this.supportedOrderTypes = enabled;
    } else {
      const types = new Set<FulfillmentMode>();
      (s.branches || []).forEach((b: any) =>
        (b.order_types || []).forEach((ot: any) => { const m = mapOrderType(ot.order_type); if (m) types.add(m); }));
      this.supportedOrderTypes = [...types];
    }

    this.announcement = s.announcement || null;
    this.announcements = String(s.announcement || '').split(/\r?\n/).map((m) => m.trim()).filter(Boolean);
    this.banners = (s.banners || []).map((b: any): Banner => ({
      title: b.title, subtitle: b.subtitle ?? null,
      imageUrl: b.image_url ?? null, mobileImageUrl: b.mobile_image_url ?? null,
      actionType: b.action_type, actionValue: b.action_value,
    }));
    this.collections = (s.collections || []).map((c: any): HomeCollection => ({
      id: c.id, type: c.type, name: c.name, displayName: c.display_name, itemsUrl: c.items_url,
    }));
    this.flagDefs = {};
    ((s.flags && s.flags.definitions) || []).forEach((d: any) => {
      const key = d.key ?? d.id ?? d.slug;
      if (key) this.flagDefs[key] = { label: d.label ?? d.name ?? key, color: d.color ?? undefined };
    });
    this.branches = (s.branches || []).map((b: any): StoreBranch => ({
      id: b.id,
      branchId: b.branch_id,
      orderTypes: (b.order_types || []).map((o: any): any => ({
        type: o.order_type,
        estimatedMinutes: o.estimated_minutes ?? null,
        minOrderAmount: o.min_order_amount ?? null,
        freeDeliveryThreshold: o.free_delivery_threshold ?? null,
        schedulingEnabled: !!o.scheduling_enabled,
      })),
    }));

    const id = s.identity || {};
    this.favicon = id.favicon_url || null;
    const com = s.commerce || {};
    const name = id.display_name || FALLBACK_BRAND.name;
    // "@restaurantname": a dedicated API field wins; otherwise the restaurant's name as one
    // lowercase word (letters/digits only, any script), falling back to the subdomain.
    this.socialHandle = String(s.social_handle || id.social_handle || '').trim().replace(/^@+/, '')
      || handleFrom(id.display_name) || String(data.subdomain || '');
    // The admin's picture for items with no photo (null → the logo stands in).
    const defaultItemImage: string | null = id.default_item_image_url ?? s.default_item_image_url ?? null;
    this.brand = {
      ...FALLBACK_BRAND,
      name,
      tagline: id.tagline || FALLBACK_BRAND.tagline,
      mark: (name || '').charAt(0).toUpperCase(),
      hotline: id.hotline || FALLBACK_BRAND.hotline,
      email: id.email || id.support_email || FALLBACK_BRAND.email,
      currency: com.currency_code || FALLBACK_BRAND.currency,
      logo: id.logo_url || FALLBACK_BRAND.logo,
      itemImage: defaultItemImage || id.logo_url || FALLBACK_BRAND.logo,
      itemImageIsPhoto: !!defaultItemImage,
      domain: data.subdomain ? `order.${data.subdomain}.com` : FALLBACK_BRAND.domain,
      story: s.story?.body || FALLBACK_BRAND.story,
      social,
      deliveryFee: com.delivery_fee ?? FALLBACK_BRAND.deliveryFee,
      freeDeliveryThreshold: com.free_delivery_threshold ?? FALLBACK_BRAND.freeDeliveryThreshold,
    };

    // Reflect the real brand in the browser tab.
    if (this.brand.name) document.title = this.brand.name;

    // Rebuild the PWA manifest + home-screen/install icon from the brand logo, so
    // the installed app uses the tenant's identity (and Chrome offers "Install").
    this.install.applyBrand({
      name: this.brand.name,
      logoUrl: this.brand.logo,
      faviconUrl: this.favicon || undefined,
      themeColor: this.theme?.brand,
      bgColor: '#f6f3ee',
    });

    this.loaded = true;
  }
}

/**
 * The first image/video of the retired "Our story" media list, for a brand
 * whose settings haven't been re-saved since story_image_url / story_video_url.
 */
function legacyStoryUrl(rows: any, type: 'image' | 'video'): string | null {
  const row = (Array.isArray(rows) ? rows : [])
    .find((m: any) => m && typeof m.url === 'string' && m.url && (m.type === 'video' ? 'video' : 'image') === type);
  return row ? row.url : null;
}

/** Fonts index.html already loads; anything else is fetched from Google Fonts on demand. */
const BUNDLED_FONTS = ['Inter', 'Cairo', 'Plus Jakarta Sans'];

/** System fonts that are not on Google Fonts; the browser uses the installed face or falls back to --fr-font. */
const SYSTEM_FONTS = ['Segoe UI'];

/** Add the brand's font (weights 400–800) from Google Fonts, once. */
function loadGoogleFont(family: string | undefined): void {
  const name = (family || '').trim();
  if (!name || BUNDLED_FONTS.includes(name) || SYSTEM_FONTS.includes(name) || typeof document === 'undefined') return;
  const id = 'brand-font-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

/** "Butcher's Burger" → "butchersburger" (letters and digits of any script, lowercase). */
function handleFrom(name: string | null | undefined): string {
  return (name || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}
